// src/hooks/useBetaTester.ts
import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { logger } from '../utils/logger';

/**
 * Hook to check if the current user is a beta tester
 * Beta testers have access to unreleased features and minigames
 */
export const useBetaTester = () => {
  const [isBetaTester, setIsBetaTester] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkBetaTesterStatus();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkBetaTesterStatus();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkBetaTesterStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        setIsBetaTester(false);
        setLoading(false);
        return;
      }

      // Try JWT claims first (fastest - no database query)
      const claims = session.user.app_metadata || {};
      const jwtBetaTesterClaim = claims.is_beta_tester ?? session.user.user_metadata?.is_beta_tester;

      if (jwtBetaTesterClaim === true) {
        logger.log('✅ Beta tester status from JWT claims');
        setIsBetaTester(true);
        setLoading(false);
        return;
      }

      // Fallback: Check database (needed until user re-logs after being granted access)
      logger.log('⚠️ Beta tester JWT claim not found, checking database');
      const { data, error } = await supabase
        .from('profiles')
        .select('is_beta_tester')
        .eq('id', session.user.id)
        .single();

      if (error) {
        logger.error('Error checking beta tester status from database:', error);
        setIsBetaTester(false);
        setLoading(false);
        return;
      }

      const isBetaTesterFromDB = data?.is_beta_tester === true;
      logger.log(`Beta tester status from database: ${isBetaTesterFromDB}`);
      setIsBetaTester(isBetaTesterFromDB);
      setLoading(false);
    } catch (error) {
      logger.error('Error in checkBetaTesterStatus:', error);
      setIsBetaTester(false);
      setLoading(false);
    }
  };

  return {
    isBetaTester,
    loading,
    checkBetaTesterStatus, // Expose for manual refresh if needed
  };
};
